import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, CheckCircle, Clock, AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

export default function BillingSettings() {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [subStatus, setSubStatus] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [subRes, txRes] = await Promise.all([
        axios.get(`${API}/billing/subscription-status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/billing/transactions`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSubStatus(subRes.data);
      setTransactions(txRes.data);
    } catch {
      toast.error('Abonelik bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/billing/create-checkout-session`,
        { plan_id: planId, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ödeme başlatılamadı');
      setUpgrading(false);
    }
  };

  const statusColors = {
    active: 'bg-green-100 text-green-700 border-green-200',
    trial: 'bg-blue-100 text-blue-700 border-blue-200',
    inactive: 'bg-red-100 text-red-700 border-red-200',
    canceled: 'bg-gray-100 text-gray-700 border-gray-200',
  };

  const statusLabels = {
    active: 'Aktif',
    trial: 'Deneme Süresi',
    inactive: 'Pasif',
    canceled: 'İptal Edildi',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 text-teal-500 animate-spin" />
      </div>
    );
  }

  const status = subStatus?.subscription_status || 'inactive';
  const daysRemaining = subStatus?.days_remaining;
  const isActive = status === 'active';
  const isTrial = status === 'trial';

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Current Status */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mevcut Abonelik</p>
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-green-100' : isTrial ? 'bg-blue-100' : 'bg-red-100'}`}>
                {isActive ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                  isTrial ? <Clock className="w-5 h-5 text-blue-600" /> :
                  <AlertTriangle className="w-5 h-5 text-red-500" />}
              </div>
              <div>
                <p className="font-semibold text-gray-800">
                  {isActive ? 'Ücretli Abonelik' : isTrial ? 'Ücretsiz Deneme' : 'Abonelik Gerekli'}
                </p>
                <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border mt-1 ${statusColors[status] || statusColors.inactive}`}>
                  {statusLabels[status] || status}
                </span>
              </div>
            </div>

            {isTrial && daysRemaining !== null && (
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">{daysRemaining}</p>
                <p className="text-xs text-gray-500">gün kaldı</p>
              </div>
            )}
          </div>

          {isTrial && daysRemaining !== null && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700">
                Deneme süreniz <strong>{daysRemaining} gün</strong> içinde dolacak.
                Kesintisiz kullanım için abone olun.
              </p>
            </div>
          )}

          {status === 'inactive' && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-100">
              <p className="text-sm text-red-700">
                Deneme süreniz dolmuştur. Uygulamaya erişmek için abonelik satın alın.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Upgrade Section - show if not active */}
      {!isActive && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Abonelik Planları</p>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Monthly */}
            <div className="border border-gray-200 rounded-xl p-4 hover:border-teal-300 transition-all">
              <p className="font-semibold text-gray-800 mb-1">Aylık Plan</p>
              <p className="text-2xl font-bold text-teal-600">$39<span className="text-sm font-normal text-gray-500">/ay</span></p>
              <p className="text-xs text-gray-400 mt-1 mb-4">Aylık faturalandırılır</p>
              <Button
                onClick={() => handleUpgrade('monthly')}
                disabled={upgrading}
                variant="outline"
                className="w-full border-teal-500 text-teal-600 hover:bg-teal-50"
                data-testid="upgrade-monthly-btn"
              >
                {upgrading ? 'Yükleniyor...' : 'Aylık Abone Ol'}
              </Button>
            </div>

            {/* Yearly */}
            <div className="border-2 border-teal-500 rounded-xl p-4 bg-teal-50/30 relative">
              <div className="absolute -top-2.5 right-4 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                %20 İNDİRİM
              </div>
              <p className="font-semibold text-gray-800 mb-1">Yıllık Plan</p>
              <p className="text-2xl font-bold text-teal-600">$31<span className="text-sm font-normal text-gray-500">/ay</span></p>
              <p className="text-xs text-gray-400 mt-1 mb-4">Yıllık $375 faturalandırılır</p>
              <Button
                onClick={() => handleUpgrade('yearly')}
                disabled={upgrading}
                className="w-full bg-teal-600 hover:bg-teal-700"
                data-testid="upgrade-yearly-btn"
              >
                {upgrading ? 'Yükleniyor...' : 'Yıllık Abone Ol'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History */}
      {transactions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ödeme Geçmişi</p>
          </div>
          <div className="divide-y divide-gray-100">
            {transactions.map((tx, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {tx.plan_id === 'yearly' ? 'Yıllık Plan' : 'Aylık Plan'}
                    </p>
                    <p className="text-xs text-gray-400">
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-800">${tx.amount}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    tx.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                    tx.payment_status === 'unpaid' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {tx.payment_status === 'paid' ? 'Ödendi' : tx.payment_status === 'unpaid' ? 'Bekliyor' : tx.payment_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
